<template>
  <aside
    class="w-screen fixed left-0 top-0 h-screen bg-gray-200 dark:bg-gray-800 shadow-md transition-all duration-300 ease-in-out"
    :class="{ 'md:w-20': isSidebarShrink, 'md:w-64': !isSidebarShrink }"
  >
    <div class="h-full flex flex-col">
      <!-- Logo Section -->
      <div class="relative flex justify-center items-center bg-white dark:bg-gray-900 h-16 p-2">
        <!-- Logo -->
        <a :href="router.resolve({ name: 'WelcomePage' }).href">
          <Image :src="logoSrc" alt="GMG Automation" image-class="h-10" />
        </a>

        <!-- Sidebar close button -->
        <Button
          icon="pi pi-times"
          severity="danger"
          variant="text"
          rounded
          aria-label="Filter"
          class="absolute md:hidden right-2 top-3"
          size="large"
          @click="onSidebarClose"
        />
      </div>

      <nav class="max-h-[calc(100%-4rem)] scrollbar-hide" :class="{ 'overflow-y-scroll': !isSidebarShrink }">
        <TieredMenu
          v-if="isSidebarShrink"
          :model="menuModel"
          class="min-w-10 h-[calc(100vh-4rem)] p-4 border-0 bg-gray-200 dark:bg-gray-800"
        >
          <template #item="{ item }">
            <a
              @click="onLinkClick(item)"
              class="flex items-center cursor-pointer py-1.5"
              :class="{
                'bg-gray-200 dark:bg-gray-700': item.active,
                'justify-center': item?.isPrimary,
                'px-4 min-w-40': !item?.isPrimary,
              }"
            >
              <i :class="{ [item.icon]: true, 'text-2xl': item?.isPrimary }" />
              <span v-if="!item?.isPrimary" class="ml-2">{{ item.label }}</span>
            </a>
          </template>
        </TieredMenu>

        <PanelMenu v-else v-model:expanded-keys="expandedKeys" :model="menuModel" class="p-4">
          <template #item="{ item }">
            <a
              v-ripple
              @click="onLinkClick(item)"
              class="flex items-center cursor-pointer rounded-md text-surface-700 dark:text-surface-0 px-4 py-1.5"
              :class="{ 'bg-gray-200 dark:bg-gray-800': item.active }"
            >
              <i :class="item.icon" />
              <span class="ml-2">{{ item.label }}</span>
              <i
                v-if="item.items"
                class="text-primary ml-auto pi"
                :class="{ 'pi-angle-down': !expandedKeys[item.key], 'pi-angle-up': expandedKeys[item.key] }"
              />
            </a>
          </template>
        </PanelMenu>
      </nav>
    </div>
  </aside>
</template>

<script setup>
import { ref, watchEffect, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import PanelMenu from 'primevue/panelmenu'
import TieredMenu from 'primevue/tieredmenu'
import { Image } from 'primevue'
import { getSidebarItems } from '@/data/admin/sidebarItems'
import { useAuthStore } from '@/stores/authStore'
import { useDarkModeStore } from '@/stores/darkModeStore'

const emit = defineEmits(['sidebarClose', 'linkClicked'])
const props = defineProps({
  isSidebarShrink: { type: Boolean, default: false },
})

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const darkModeStore = useDarkModeStore()

const menuModel = ref([])
const expandedKeys = ref({})

const userPermissions = computed(() => authStore.user?.role?.permissions?.map((p) => p.name) || [])

const hasPerm = (item) => {
  if (!item?.permission) return true
  return userPermissions.value.includes(item.permission)
}

const filterMenuByPermissions = (items) => {
  return (items || [])
    .map((item) => {
      const children = filterMenuByPermissions(item.items)

      const canSeeSelf = hasPerm(item)

      if (item.items?.length) {
        const shouldShow = canSeeSelf && children.length > 0
        return shouldShow ? { ...item, items: children } : null
      }

      return canSeeSelf ? { ...item } : null
    })
    .filter(Boolean)
}

const sideBarItems = computed(() => {
  const items = getSidebarItems(router)
  return filterMenuByPermissions(items)
})

const findKeyPath = (items, targetKey) => {
  for (const item of items) {
    if (item.key === targetKey) return [item.key]

    if (item.items?.length) {
      const childPath = findKeyPath(item.items, targetKey)
      if (childPath) return [item.key, ...childPath]
    }
  }

  return null
}

const markActive = (items, activeKey) => {
  return items.map((item) => ({
    ...item,
    active: item.key === activeKey,
    items: item.items?.length ? markActive(item.items, activeKey) : item.items,
  }))
}

const logoSrc = computed(() => {
  if (darkModeStore.isDark) {
    return props.isSidebarShrink
      ? new URL('@/assets/images/logo_mini.svg', import.meta.url).href
      : new URL('@/assets/images/logo_light.svg', import.meta.url).href
  }

  return props.isSidebarShrink
    ? new URL('@/assets/images/logo_mini.svg', import.meta.url).href
    : new URL('@/assets/images/logo.svg', import.meta.url).href
})

const onSidebarClose = () => {
  emit('sidebarClose')
}

const onLinkClick = (item) => {
  if (item?.items) return
  if (item?.command) item.command()
  emit('linkClicked')
}

watchEffect(() => {
  const activeKey = route.name
  const base = sideBarItems.value

  menuModel.value = markActive(base, activeKey)

  const path = findKeyPath(base, activeKey) || []
  const parentsOnly = path.slice(0, -1)

  expandedKeys.value = parentsOnly.reduce((acc, key) => {
    acc[key] = true
    return acc
  }, {})
})
</script>
